import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RequirePermission } from "@components/common/require-permission";
import { BaseCompanyProps, CompanyTypeKind } from "@custom-types/base-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { WorkspaceTypeDropdown } from "@pages/workspace-profile/workspace-type-dropdown";
import { updateCompany } from "@store/workspace/workspace-thunks";
import { MINIMUM_COMPANY_NAME_LENGTH } from "@utils/ui-utils";
import { Button, Dialog, TextField, useToast } from "@flat-ui";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { TextWithTooltip } from "@components/common/text-with-tooltip";

/** A component for editing workspace details */
export function EditWorkspace({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [companyName, setCompanyName] = useState<string>(company.name);
  const [workspaceType, setWorkspaceType] = useState<CompanyTypeKind>(
    company.type ?? "unspecified"
  );

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const dispatch = useAppDispatch();
  const { openToast } = useToast();

  const { fetchingCompany } = useAppSelector(companyStateSelector);

  /** Whether there are any changes in workspace details dialog */
  const hasNoChanges =
    companyName.trim() === (company.name ?? "").trim() &&
    workspaceType === (company.type ?? "unspecified");

  function onCloseDialog(): void {
    setIsDialogOpen(false);
    setCompanyName(company.name);
    setWorkspaceType(company.type ?? "unspecified");
  }

  function checkValidity(): {
    isCompanyNameValid: boolean;
  } {
    return {
      isCompanyNameValid:
        companyName.trim().length >= MINIMUM_COMPANY_NAME_LENGTH,
    };
  }

  async function onUpdateCompanyDetails(): Promise<void> {
    trackEvent({
      name: WorkspaceEvents.editWorkspaceDetails,
      props: {
        isNameChanged: companyName.trim() !== company.name.trim(),
        type: workspaceType,
      },
    });

    try {
      await dispatch(
        updateCompany({
          companyId: company.id,
          payload: { type: workspaceType, companyName: companyName.trim() },
        })
      ).unwrap();
      openToast({
        title: "Workspace details updated successfully!",
      });
      setIsDialogOpen(false);
    } catch (error) {
      handleErrorWithToast({
        id: `edit-company-details-${Date.now().toString()}`,
        title: "Failed to update workspace details",
        error,
      });
    }
  }

  return (
    <>
      <RequirePermission permission="workspace.edit">
        <Button
          onClick={() => {
            trackEvent({ name: WorkspaceEvents.openEditWorkspaceDetails });
            setIsDialogOpen(true);
          }}
          variant="secondary"
        >
          Edit Workspace Details
        </Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title={
          <TextWithTooltip
            prefix="Edit"
            text={company.name}
            suffix="workspace details"
          />
        }
        onCancel={onCloseDialog}
        onConfirm={onUpdateCompanyDetails}
        isConfirmDisabled={
          Object.values(checkValidity()).some((isValid) => !isValid) ||
          hasNoChanges
        }
        showSpinner={fetchingCompany.updatingCompany === "pending"}
      >
        <TextField
          label="Workspace ID"
          text={company.id}
          disabled={true}
          fullWidth
          onTextChanged={() => {}}
          controlStyle={{ mt: 2 }}
        />

        <TextField
          label="Workspace name"
          text={companyName}
          onTextChanged={setCompanyName}
          fullWidth
          error={
            !checkValidity().isCompanyNameValid &&
            `Minimum ${MINIMUM_COMPANY_NAME_LENGTH} characters`
          }
          controlStyle={{ mt: 2 }}
        />

        <WorkspaceTypeDropdown
          selectedType={workspaceType}
          onTypeSelect={setWorkspaceType}
        />
      </Dialog>
    </>
  );
}
